import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

// ── Types ─────────────────────────────────────────────────────

export interface PipelineItem {
  stage: string;
  count: number;
  color: string;
}

export interface FunnelItem {
  status: string;
  count: number;
  color: string | null;
}

export interface RecentMandate {
  id: string;
  name: string;
  stage: string;
  stageColor: string;
  value: string | null;
}

export interface ActionItem {
  type: 'overdue' | 'review';
  id: string;
  title: string;
  subtitle: string;
  mandateId: string | null;
  requirementId: string | null;
  propositionId: string | null;
  daysOverdue: number;
  createdAt: string | null;
}

export interface UpcomingInterview {
  id: string;
  candidateName: string;
  interviewType: string | null;
  scheduledAt: string;
  mandateName: string;
  mandateReference: string | null;
  requirementId: string;
  propositionId: string;
  participantCount: number;
  feedbackPending: boolean;
}

export interface ClientDashboard {
  activeSuppliers: number;
  activeMandates: number;
  openRequirements: number;
  overdueRequirements: number;
  pendingReviews: number;
  mandateTrend: number;
  requirementTrend: number;
  recentMandates: RecentMandate[];
  attentionItems: ActionItem[];
  upcomingInterviews: UpcomingInterview[];
  fulfilledRequirements: number;
  totalPositions: number;
  filledPositions: number;
  aging: AgingStats;
}

export interface AgingStats {
  under7: number;
  days7to14: number;
  days14to30: number;
  over30: number;
}

export interface PipelineData {
  pipeline: PipelineItem[];
  pipelineByValue: PipelineValueItem[];
}

export interface PipelineValueItem {
  stage: string;
  value: number;
  color: string;
}

export interface TimeToFillPoint {
  period: string;
  avgDays: number;
}

export interface SupplierPerformance {
  id: string;
  name: string;
  responseRate: number;
  signedCount: number;
  dispatchCount: number;
}

export interface FunnelData {
  propositionFunnel: FunnelItem[];
  trend: number;
}

export interface RecentProposition {
  id: string;
  candidateName: string;
  mandateName: string;
  status: string;
}

export interface RecentDispatch {
  mandateId: string;
  mandateName: string;
  dispatchedAt: string;
}

export interface SupplierDashboard {
  activeMandates: number;
  openRequirements: number;
  pendingPropositions: number;
  submittedCandidates: number;
  recentPropositions: RecentProposition[];
  pendingDispatches: RecentDispatch[];
}

// ── Queries ───────────────────────────────────────────────────

export function useClientDashboard() {
  return useQuery<ClientDashboard>({
    queryKey: ['dashboard', 'client'],
    queryFn: () => api.get<ClientDashboard>('/api/dashboard/client'),
  });
}

export function useClientPipeline(period: number = 30) {
  return useQuery<PipelineData>({
    queryKey: ['dashboard', 'pipeline', period],
    queryFn: () => api.get<PipelineData>(`/api/dashboard/client/pipeline?period=${period}`),
    placeholderData: (prev) => prev,
  });
}

export function useClientFunnel(period: number = 30) {
  return useQuery<FunnelData>({
    queryKey: ['dashboard', 'funnel', period],
    queryFn: () => api.get<FunnelData>(`/api/dashboard/client/funnel?period=${period}`),
    placeholderData: (prev) => prev,
  });
}

export function useClientTimeToFill(period: number = 90) {
  return useQuery<TimeToFillPoint[]>({
    queryKey: ['dashboard', 'time-to-fill', period],
    queryFn: () => api.get<TimeToFillPoint[]>(`/api/dashboard/client/time-to-fill?period=${period}`),
    placeholderData: (prev) => prev,
  });
}

export function useClientSupplierPerformance() {
  return useQuery<SupplierPerformance[]>({
    queryKey: ['dashboard', 'suppliers'],
    queryFn: () => api.get<SupplierPerformance[]>('/api/dashboard/client/suppliers'),
  });
}

export function useSupplierDashboard() {
  return useQuery<SupplierDashboard>({
    queryKey: ['dashboard', 'supplier'],
    queryFn: () => api.get<SupplierDashboard>('/api/dashboard/supplier'),
  });
}
